import {
  IInteractionData,
  IInteractionTypes,
  INavigationCardData,
  INavigationData,
  ITaskData,
  ITaskTypes,
} from "@origin-digital/reporting-client";
import { EnvironmentNames } from "@origin-digital/platform-enums";
import { logCustomEvent, requestImmediateDataFlush } from "@braze/web-sdk";

import { ICustomEvent } from "@origin-digital/event-dispatcher";
import {
  AnalyticsEventType,
  BrazeEventType,
  InteractionCardDetailType,
  InteractionCardType,
  InteractionDetailType,
  InteractionType,
  NavigationCardDetailType,
  NavigationDetailType,
  NavigationType,
  TaskDetailType,
  TaskType,
} from "../consts/interfaces";
import { BASE_CONTENT_CARD_EVENT_NAME } from "../consts/consts";
import { getPrefetchedWhitelistBrazeMetadata } from "./prefetch";
import { clearPIIFromData, formatUri, setKebabCase } from "./eventTools";

let brazeEventsToBeSent: BrazeEventType[] = [];

// Queue up events
export const addBrazeEventsToBeSent = (event: BrazeEventType) => {
  brazeEventsToBeSent.push(event);
};

// Filter on:
// event.detail.data.appName
// event.detail.type
// event.details.data.action
// return [<app-name>::task::<type>::<action>] or undefined
export const getTaskEvents = (
  detail: {
    eventType: AnalyticsEventType;
    type: ITaskTypes;
    data: ITaskData;
  },
  whitelist: TaskType[]
): string[] | undefined => {
  const { data } = detail;

  const result = whitelist.reduce(
    (acc: string[], { appName, type, action }) => {
      if (
        appName &&
        appName === data?.appName &&
        type === detail?.type &&
        action === data?.action
      ) {
        acc.push(`${setKebabCase(appName)}::task::${type}::${action}`);
      }
      return acc;
    },
    []
  );

  return result.length > 0 ? result : undefined;
};

// Match on:
// event.detail.data.appName
// domainCorrectedUri
// return matching whitelist items or undefined
export const getNavigationRegexMatches = (
  whitelist: NavigationType[],
  data: INavigationData,
  domainCorrectedUri: string
): NavigationType[] | undefined => {
  try {
    return whitelist.filter(
      ({ appName, uri }) =>
        appName === data.appName &&
        !!uri.regex &&
        domainCorrectedUri.match(new RegExp(uri.regex))
    );
  } catch {
    return undefined;
  }
};

// Filter on:
// event.detail.data.currentUri
// event.detail.data.appName
// returns [<appName>::<type>::<currentUri>, <appName>::<type>::<regex>] or undefined
export const getNavigationEvents = (
  environment: EnvironmentNames,
  data: INavigationData | INavigationCardData,
  whitelist: NavigationType[],
  type: "navigation" | "navigationCard" = "navigation"
): string[] | undefined => {
  if (!data.currentUri) {
    return undefined;
  }

  // If Staging update to check against a Prod version of the currentUri
  const domainCorrectedCurrentUri = formatUri(environment, data.currentUri);

  const formattedFilteredLabelEvent = whitelist.reduce(
    (acc: string[], { appName, uri }) => {
      if (
        appName === data.appName &&
        uri?.label === domainCorrectedCurrentUri
      ) {
        acc.push(`${setKebabCase(appName)}::${type}::${uri.label}`);
      }
      return acc;
    },
    []
  );

  const filteredRegexEvent = getNavigationRegexMatches(
    whitelist,
    data,
    domainCorrectedCurrentUri
  );

  const formattedFilteredRegexEvent = filteredRegexEvent?.map(
    (filteredEvent) =>
      `${setKebabCase(filteredEvent.appName)}::${type}::${
        filteredEvent.uri.label
      }`
  );

  const result = [
    ...(formattedFilteredLabelEvent ?? []),
    ...(formattedFilteredRegexEvent ?? []),
  ];

  return result.length > 0 ? result : undefined;
};

const filterAndFormatEvents = <T>(
  whitelist: T[],
  matches: (entry: T) => boolean,
  format: (entry: T) => string
): string[] | undefined => {
  const results = whitelist.filter(matches).map(format);
  return results.length > 0 ? results : undefined;
};

// Filter on:
// event.detail.data.appName
// event.detail.type
// event.details.data.id
// [<app-name>::interaction::<type>::<id>] or undefined
export const getInteractionEvents = (
  detail: {
    eventType: AnalyticsEventType;
    interaction: IInteractionTypes;
    data: IInteractionData;
  },
  whitelist: InteractionType[]
): string[] | undefined => {
  const { data } = detail;
  return filterAndFormatEvents(
    whitelist,
    ({ appName, componentType, id = "" }) =>
      appName === data.appName &&
      id === setKebabCase(data.id) &&
      componentType === data.componentType.toLowerCase(),
    ({ appName, componentType, id }) =>
      `${setKebabCase(
        appName ?? ""
      )}::interaction::${componentType.toLowerCase()}::${setKebabCase(id)}`
  );
};

// Filter on:
// event.detail.data.appName
// event.detail.data.card.id
// event.detail.data.label
// [<appName>::interactionCard::<id>::<label>] or undefined
export const getInteractionCardEvents = (
  detail: InteractionCardDetailType,
  whitelist: InteractionCardType[]
): string[] | undefined => {
  const { data } = detail;
  return filterAndFormatEvents(
    whitelist,
    ({ appName, id, label }) =>
      appName === data.appName &&
      id === setKebabCase(data.card.id) &&
      label === setKebabCase(data.label),
    ({ appName, id, label }) =>
      `${setKebabCase(appName)}::interactionCard::${setKebabCase(
        id
      )}::${setKebabCase(label)}`
  );
};

export const getTrackingEventNames = (
  environment: EnvironmentNames,
  detail:
    | InteractionDetailType
    | InteractionCardDetailType
    | NavigationDetailType
    | NavigationCardDetailType
    | TaskDetailType
): string[] | undefined => {
  // white listed events (navigation, task, interaction)
  const getBrazeEvents = (eventType: string | undefined) => {
    const { events } = getPrefetchedWhitelistBrazeMetadata();

    switch (eventType) {
      case "interaction":
        return getInteractionEvents(
          detail as InteractionDetailType,
          events["interaction"]
        );

      case "interactionCard":
        return getInteractionCardEvents(
          detail as InteractionCardDetailType,
          events["interactionCard"]
        );

      case "navigation":
        return getNavigationEvents(
          environment,
          detail.data as INavigationData,
          events["navigation"],
          "navigation"
        );

      case "navigationCard":
        return getNavigationEvents(
          environment,
          detail.data as INavigationCardData,
          events["navigationCard"],
          "navigationCard"
        );

      case "task":
        return getTaskEvents(detail as TaskDetailType, events["task"]);

      default:
        return undefined;
    }
  };

  return getBrazeEvents(detail?.eventType);
};

let checkedUser = false;

export const handleEventListener = (
  environment: EnvironmentNames,
  event: any,
  _logCustomEvent: (
    brazeEventName: string,
    data: INavigationData | ITaskData
  ) => void
) => {
  const { detail } = event;
  // check if it is whitelisted
  const brazeEventNames = getTrackingEventNames(environment, detail);

  if (!brazeEventNames || brazeEventNames?.length === 0) {
    return;
  }
  const { data } = detail;
  const cleanedData = clearPIIFromData(data);

  // confirmed user account and auth?
  // for each event, send to braze
  brazeEventNames.forEach((brazeEventName: string) => {
    if (checkedUser) {
      _logCustomEvent(brazeEventName, cleanedData);
    } else {
      addBrazeEventsToBeSent({
        brazeEventName,
        data: cleanedData,
      });
    }
  });
};

export const sendBrazeEvents = (
  _logCustomEvent: (
    brazeEventName: string,
    data: INavigationData | ITaskData
  ) => void
) => {
  checkedUser = true;
  brazeEventsToBeSent.forEach((event: BrazeEventType) => {
    const { brazeEventName, data } = event;
    const cleanedData = clearPIIFromData(data);
    _logCustomEvent(brazeEventName, cleanedData);
  });

  if (brazeEventsToBeSent.length > 0) {
    requestImmediateDataFlush();
    brazeEventsToBeSent = []; // clear queue
  }
};

// Test helper to clear queued events
export const clearBrazeEventsToBeSent = () => {
  brazeEventsToBeSent.length = 0; // Clear the array
};

export const logContentCardCustomEvent = ({ name, data }: ICustomEvent) => {
  const formattedEventName = setKebabCase(name);
  logCustomEvent(`${BASE_CONTENT_CARD_EVENT_NAME}${formattedEventName}`, data);
};
